import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from '../../../adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from '../../../adapters/types'
import { DischargeFormData } from '../../types/formData'
import { DischargeFormField } from '../../types/formField'
import { DischargeAssessmentQuestionnaireItem } from '../../types/questionnaire'
import {
  getDateAnswerFromItem,
  getReferenceAnswerFromItem,
  getTextAnswerFromItem
} from '../../../helpers'

/**
 * Final Steps subsection adapter
 * Handles discharge communication, narrative, and internal notes fields
 */
export class FinalStepsAdapter extends BaseSubsectionAdapter<DischargeFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<DischargeFormData> {
    const currentCertPeriod = getReferenceAnswerFromItem(
      flatAnswers[DischargeAssessmentQuestionnaireItem.FINAL_STEPS_CURRENT_CERT_PERIOD]
    )
    const dischargeCommunication = getTextAnswerFromItem(
      flatAnswers[DischargeAssessmentQuestionnaireItem.FINAL_STEPS_DISCHARGE_COMMUNICATION]
    )
    const resourcesProvided = getTextAnswerFromItem(
      flatAnswers[DischargeAssessmentQuestionnaireItem.FINAL_STEPS_RESOURCES_PROVIDED]
    )
    const caregiverResponse = getTextAnswerFromItem(
      flatAnswers[DischargeAssessmentQuestionnaireItem.FINAL_STEPS_CAREGIVER_RESPONSE]
    )
    const datePhysicianNotified = getDateAnswerFromItem(
      flatAnswers[DischargeAssessmentQuestionnaireItem.FINAL_STEPS_DATE_PHYSICIAN_NOTIFIED],
      true
    )
    const dischargeNarrative = getTextAnswerFromItem(
      flatAnswers[DischargeAssessmentQuestionnaireItem.FINAL_STEPS_DISCHARGE_NARRATIVE]
    )
    const internalNotes = getTextAnswerFromItem(
      flatAnswers[DischargeAssessmentQuestionnaireItem.FINAL_STEPS_INTERNAL_NOTES]
    )

    return {
      [DischargeFormField.FINAL_STEPS_CURRENT_CERT_PERIOD]: currentCertPeriod,
      [DischargeFormField.FINAL_STEPS_DISCHARGE_COMMUNICATION]: dischargeCommunication,
      [DischargeFormField.FINAL_STEPS_RESOURCES_PROVIDED]: resourcesProvided,
      [DischargeFormField.FINAL_STEPS_CAREGIVER_RESPONSE]: caregiverResponse,
      [DischargeFormField.FINAL_STEPS_DATE_PHYSICIAN_NOTIFIED]: datePhysicianNotified,
      [DischargeFormField.FINAL_STEPS_DISCHARGE_NARRATIVE]: dischargeNarrative,
      [DischargeFormField.FINAL_STEPS_INTERNAL_NOTES]: internalNotes
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(DischargeFormField.FINAL_STEPS_CURRENT_CERT_PERIOD)) {
      const currentCertPeriod = this.getFormDataField(
        DischargeFormField.FINAL_STEPS_CURRENT_CERT_PERIOD
      )
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.FINAL_STEPS_CURRENT_CERT_PERIOD,
        value: currentCertPeriod ? { reference: currentCertPeriod } : undefined
      })
    }
    if (this.formDataFieldExists(DischargeFormField.FINAL_STEPS_DISCHARGE_COMMUNICATION)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.FINAL_STEPS_DISCHARGE_COMMUNICATION,
        value: this.getFormDataField(DischargeFormField.FINAL_STEPS_DISCHARGE_COMMUNICATION)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.FINAL_STEPS_RESOURCES_PROVIDED)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.FINAL_STEPS_RESOURCES_PROVIDED,
        value: this.getFormDataField(DischargeFormField.FINAL_STEPS_RESOURCES_PROVIDED)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.FINAL_STEPS_CAREGIVER_RESPONSE)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.FINAL_STEPS_CAREGIVER_RESPONSE,
        value: this.getFormDataField(DischargeFormField.FINAL_STEPS_CAREGIVER_RESPONSE)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.FINAL_STEPS_DATE_PHYSICIAN_NOTIFIED)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.FINAL_STEPS_DATE_PHYSICIAN_NOTIFIED,
        value: this.getFormDataField(DischargeFormField.FINAL_STEPS_DATE_PHYSICIAN_NOTIFIED)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.FINAL_STEPS_DISCHARGE_NARRATIVE)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.FINAL_STEPS_DISCHARGE_NARRATIVE,
        value: this.getFormDataField(DischargeFormField.FINAL_STEPS_DISCHARGE_NARRATIVE)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.FINAL_STEPS_INTERNAL_NOTES)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.FINAL_STEPS_INTERNAL_NOTES,
        value: this.getFormDataField(DischargeFormField.FINAL_STEPS_INTERNAL_NOTES)
      })
    }

    return mutations
  }
}
